import { useEffect, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  SidePanel,
  ButtonPrimary,
  ButtonPseudo,
  Popup,
  Select,
  ajvResolver,
  ValidatorsProps,
  responseErrorsResolver,
  ResponseError,
  PermissionEnum,
  Tabs,
  ChangeLogs,
  SidePanelType,
  PermissionDenied,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import ROUTES from '../../routes';
import { changePanelState, onRoleChange } from './store/slices/roles';
import RoleEditForm from '../../components/user-roles/panel/panel-role-edit';
import {
  useEditRoleMutation,
  useLazyGetRoleQuery,
  useLazyGetRoleChangeLogsQuery,
} from './store/services/roles';
import { EditRoleFormValues, EditRoleRequest, RoleDetails } from './types';

import cn from './styles.module.css';

const RoleEditHOC = (): React.JSX.Element => {
  const {
    userRoles: {
      roles: {
        roles,
        panelIsOpen,
        roleInfo,
        selectedRole,
        validators,
        changeLogs,
        changeLogsPagination,
      },
    },
    administrationDictionaries: { users, activePermissions },
    notificationSettings: {
      templates: { templates },
    },
  } = useAppSelector((state) => state);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const [archivePopupVisible, setArchivePopupVisible] = useState(false);
  const [roleValue, setRoleValue] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'logs'>('details');

  const { roleId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [edit, { isLoading: editRoleIsLoading, isSuccess }] =
    useEditRoleMutation();

  const [getRole, { isLoading: roleIsLoading }] = useLazyGetRoleQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetRoleChangeLogsQuery();

  const getRoleAction = useCallback(() => {
    getRole(roleId as string);
  }, [roleId]);

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=EMPLOYEE_ROLE&entityId=${roleId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [roleId],
  );

  const rolesOptionsWithoutSelected = roles
    ?.filter((role) => role.id !== roleId)
    .map((item) => ({
      name: item.name,
      value: item.id,
    }));

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitted, isDirty },
    setError,
  } = useForm<EditRoleFormValues>({
    defaultValues: roleInfo,
    resolver: ajvResolver(
      validators?.updateRoleTemplateSchema as ValidatorsProps,
    ),
  });

  const handleClose = useCallback(() => {
    dispatch(changePanelState(false));

    setTimeout(() => {
      navigate(ROUTES.USER_ROLES);
      dispatch(onRoleChange(roleId || ((roles && roles[0].id) as string)));
    }, 100);
  }, [dispatch]);

  const onRequest = async (body: EditRoleRequest) => {
    setGlobalError('');

    const response = (await edit({ body, id: roleId as string })) as {
      data?: RoleDetails;
      error: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  };

  const onSubmit = handleSubmit((values) => {
    const body: EditRoleRequest = {
      name: values.name,
      active: values.active,
      permissions: roleInfo?.permissions || [],
    };

    if (values.notificationTypeTemplateId) {
      body.notificationTypeTemplateId = values.notificationTypeTemplateId;
    }

    if (roleValue) {
      onRequest(body);
      setArchivePopupVisible(false);
    } else if (!values.active) {
      setArchivePopupVisible(true);
    } else {
      onRequest(body);
    }
  });

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      setRoleValue('');
      reset();
      dispatch(changePanelState(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (roleId && panelIsOpen) {
      reset();
      getRoleAction();
    }
  }, [panelIsOpen, roleId, getRoleAction]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess, handleClose]);

  useEffect(() => {
    reset(roleInfo);
  }, [roleInfo]);

  useEffect(() => {
    if (
      activeTab === 'logs' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  const isLoading = roleIsLoading || editRoleIsLoading;

  const isEditable = activePermissions?.includes(
    PermissionEnum.USERS_ROLES_EDIT_ACTION,
  );

  return (
    <>
      <Popup
        title="Assign employees a different role first"
        description={`Choose a new role for ${selectedRole?.usersCount} employees`}
        onCancelClick={() => setArchivePopupVisible(false)}
        isVisible={archivePopupVisible}
        parent={document.getElementById('archive-popup') as HTMLElement}
      >
        <div className={cn.popupWrapper}>
          <Select
            name="role"
            onChange={(e) => setRoleValue(e.target.value as string)}
            value={roleValue}
            list={rolesOptionsWithoutSelected}
            placeholder="Select role..."
          />
          <div className={cn.popupButtons}>
            <ButtonPseudo
              bold
              onClick={() => setArchivePopupVisible(false)}
              text="Cancel"
            />
            <ButtonPrimary
              bold
              onClick={onSubmit}
              disabled={Boolean(!roleValue)}
              text={`Move “${selectedRole?.name}” to archive`}
            />
          </div>
        </div>
      </Popup>

      <SidePanel
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title="Role details"
        errorSidepanel={globalError}
        type={SidePanelType.Accounts}
        sidepanelExpandedKey="role-details"
        footer={
          <>
            {isEditable && (
              <ButtonPrimary
                text={isLoading ? 'Loading...' : 'Save'}
                onClick={onSubmit}
                disabled={isLoading || !isDirty || (!isValid && isSubmitted)}
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        <>
          {roleId && (
            <Tabs
              tabs={[
                {
                  name: 'Details',
                  value: 'details',
                },
                {
                  name: 'Change log',
                  value: 'logs',
                },
              ]}
              activeTab={activeTab}
              onClick={(tab) => setActiveTab(tab as 'details' | 'logs')}
            />
          )}
          {activeTab === 'details' && (
            <RoleEditForm
              control={control}
              roleInfo={roleInfo}
              isLoading={roleIsLoading}
              errors={errors}
              isEditable={isEditable}
              auditLogAvailable={activePermissions?.includes(
                PermissionEnum.AUDIT_LOG_LIST,
              )}
              roleId={roleId}
              templates={templates?.map((item) => ({
                name: item.name,
                value: item.id,
              }))}
              templatesVisible={activePermissions?.includes(
                PermissionEnum.NOTIFICATIONS_TYPES_TEMPLATE_LIST,
              )}
            />
          )}
          {activeTab === 'logs' && (
            <>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <ChangeLogs
                  entityType="EMPLOYEE_ROLE"
                  entityId={roleId as string}
                  changeLogsIsLoading={changeLogsIsLoading}
                  changeLogs={changeLogs}
                  activePermission={activePermissions}
                  users={users}
                  pagination={changeLogsPagination}
                  changeLogsIsFetching={changeLogsIsFetching}
                  getChangeLogs={getChangeLogsAction}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </>
          )}
        </>
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default RoleEditHOC;
